/**
 * Grid-cell bookkeeping for the State viewport refresh.
 *
 * Buckets bounds into `gridSize`-degree cells with the same algorithm as the marker
 * spatial index, so "already fetched" checks match how markers are stored.
 *
 * Pure — the fetched-cell set is owned and passed in by the caller.
 *
 * @module searchResultsMap/services/stateViewportGrid
 */

/**
 * @param {{ south: number, north: number, west: number, east: number }} bounds
 * @param {number} gridSize - Cell size in decimal degrees.
 * @returns {string[]} Keys of every cell that intersects the bounds.
 */
export function getViewportGridCells(bounds, gridSize) {
  const cells = [];
  const latMin = Math.floor(bounds.south / gridSize);
  const latMax = Math.ceil(bounds.north / gridSize);
  const lngMin = Math.floor(bounds.west / gridSize);
  const lngMax = Math.ceil(bounds.east / gridSize);

  Array.from({ length: latMax - latMin + 1 }, (_, i) => latMin + i).forEach((lat) => {
    Array.from({ length: lngMax - lngMin + 1 }, (_, i) => lngMin + i).forEach((lng) => {
      cells.push(`${lat},${lng}`);
    });
  });

  return cells;
}

/**
 * @param {Set<string>} fetchedCells
 * @param {{ south: number, north: number, west: number, east: number }} bounds
 * @param {number} gridSize
 * @returns {boolean} True when at least one cell in the bounds has not been fetched.
 */
export function viewportHasUnfetchedCells(fetchedCells, bounds, gridSize) {
  return getViewportGridCells(bounds, gridSize).some((cell) => !fetchedCells.has(cell));
}

/**
 * Adds every cell that intersects the bounds to the fetched set.
 *
 * @param {Set<string>} fetchedCells
 * @param {{ south: number, north: number, west: number, east: number }} bounds
 * @param {number} gridSize
 */
export function markViewportFetched(fetchedCells, bounds, gridSize) {
  getViewportGridCells(bounds, gridSize).forEach((cell) => fetchedCells.add(cell));
}
